import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Save, Loader2, Trash2, X, Wallet, Landmark, Percent, Filter,
  ChevronDown, ChevronUp, Search, Layers,
} from "lucide-react";
import { usePinSecurity } from "@/contexts/PinContext";

const CURRENT_YEAR = new Date().getFullYear();
const MONEY = (n) => `${(Number(n) || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

/* ─────────────────────────────────────────────────────────────
 * V1.38 · Panel Ingresos (Fase 2)
 * ────────────────────────────────────────────────────────── */
export function IncomesPanel({ sectionId, sectionLabel }) {
  const pin = usePinSecurity();
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ base: 0, vat: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filters, setFilters] = useState({ year: CURRENT_YEAR, quarter: "", payment_method: "", q: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v !== null) params[k] = v; });
      if (sectionId) params.section_id = sectionId;
      const { data } = await api.get("/accounting/incomes", { params });
      setItems(data?.items || []);
      setTotals(data?.totals || { base: 0, vat: 0, total: 0 });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Error");
    } finally {
      setLoading(false);
    }
  }, [filters, sectionId]);
  useEffect(() => { load(); }, [load]);

  const remove = async (row) => {
    try {
      await pin.require("accounting.income.delete");
      if (!window.confirm(`¿Eliminar el ingreso "${row.concept}"?`)) return;
      await api.delete(`/accounting/incomes/${row.id}`);
      toast.success("Ingreso eliminado");
      load();
    } catch (err) {
      const m = formatApiError(err?.response?.data?.detail) || err?.message;
      if (m && !/cancel/i.test(m)) toast.error(m);
    }
  };

  return (
    <div className="space-y-3" data-testid="incomes-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap text-xs text-neutral-500">
          {sectionLabel && (
            <span className="inline-flex items-center gap-1">
              <Layers className="h-3.5 w-3.5 text-orange-600" />
              <span className="rounded-full bg-orange-100 text-orange-800 px-2 py-0.5 font-bold">{sectionLabel}</span>
            </span>
          )}
          <span className="inline-flex items-center gap-1"><Wallet className="h-3.5 w-3.5" /> Ingresos {filters.year}</span>
        </div>
        <button onClick={() => setOpenNew(true)} className="btn-primary !h-9 !px-3 !text-xs" data-testid="income-new-btn">
          <Plus className="h-4 w-4" /> Nuevo ingreso
        </button>
      </div>

      <IncomeFiltersBar filters={filters} onChange={setFilters} />

      {loading ? (
        <div className="text-center text-sm text-neutral-500 py-8"><Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Cargando…</div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <TotalCard label="Base" value={MONEY(totals.base)} color="neutral" />
            <TotalCard label="IVA repercutido" value={MONEY(totals.vat)} color="emerald" />
            <TotalCard label="Total" value={MONEY(totals.total)} color="orange" />
          </div>
          <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-[10px] uppercase tracking-wider text-neutral-500">
                <tr>
                  <th className="text-left px-3 py-2 w-24">Fecha</th>
                  <th className="text-left px-3 py-2">Concepto</th>
                  <th className="text-left px-3 py-2 w-40">Alumno</th>
                  <th className="text-left px-3 py-2 w-24">Cobro</th>
                  <th className="text-right px-3 py-2 w-24">Base</th>
                  <th className="text-right px-3 py-2 w-20">IVA</th>
                  <th className="text-right px-3 py-2 w-28">Total</th>
                  <th className="w-16"></th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={8} className="text-center text-sm text-neutral-500 py-8">Sin ingresos registrados.</td></tr>
                ) : items.map((row) => (
                  <tr key={row.id} className="border-t border-neutral-100 hover:bg-neutral-50/50" data-testid={`income-row-${row.id}`}>
                    <td className="px-3 py-2 tabular-nums">{row.date}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{row.concept}</div>
                      {row.invoice_number && <div className="text-[10px] text-neutral-500">Factura: {row.invoice_number}</div>}
                    </td>
                    <td className="px-3 py-2 text-neutral-600 text-xs">{row.student_name || "—"}</td>
                    <td className="px-3 py-2 text-xs">
                      <PaymentBadge method={row.payment_method} />
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{MONEY(row.base_amount)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-neutral-500">{row.vat_percent}%</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold">{MONEY(row.total_amount)}</td>
                    <td className="px-3 py-2 text-right space-x-1 whitespace-nowrap">
                      <button onClick={() => setEditing(row)} className="text-xs text-neutral-600 hover:text-orange-600" data-testid={`income-edit-${row.id}`}>Editar</button>
                      <button onClick={() => remove(row)} className="text-xs text-red-600 hover:text-red-800" data-testid={`income-del-${row.id}`}>
                        <Trash2 className="h-3.5 w-3.5 inline" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {(openNew || editing) && (
        <IncomeEditor
          initial={editing || {}}
          sectionId={sectionId}
          onClose={() => { setOpenNew(false); setEditing(null); }}
          onSaved={() => { setOpenNew(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function IncomeFiltersBar({ filters, onChange }) {
  return (
    <div className="flex items-center gap-2 flex-wrap text-xs">
      <div className="inline-flex items-center gap-1 text-neutral-500"><Filter className="h-3 w-3" /> Filtros:</div>
      <select value={filters.year} onChange={(e) => onChange({ ...filters, year: parseInt(e.target.value, 10) })}
              className="h-8 rounded-lg border border-neutral-300 bg-white px-2 text-xs">
        {[CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1].map((y) => <option key={y} value={y}>{y}</option>)}
      </select>
      <select value={filters.quarter} onChange={(e) => onChange({ ...filters, quarter: e.target.value })}
              className="h-8 rounded-lg border border-neutral-300 bg-white px-2 text-xs">
        <option value="">Todos los trimestres</option>
        {[1, 2, 3, 4].map((q) => <option key={q} value={q}>T{q}</option>)}
      </select>
      <select value={filters.payment_method} onChange={(e) => onChange({ ...filters, payment_method: e.target.value })}
              className="h-8 rounded-lg border border-neutral-300 bg-white px-2 text-xs">
        <option value="">Todos los cobros</option>
        <option value="cash">Efectivo</option>
        <option value="card">Tarjeta</option>
        <option value="transfer">Transferencia</option>
        <option value="bizum">Bizum</option>
        <option value="other">Otro</option>
      </select>
      <div className="relative">
        <Search className="h-3 w-3 absolute left-2 top-2.5 text-neutral-400" />
        <input value={filters.q} onChange={(e) => onChange({ ...filters, q: e.target.value })}
               placeholder="Buscar concepto/alumno/factura…"
               className="h-8 rounded-lg border border-neutral-300 bg-white pl-7 pr-2 text-xs w-64" />
      </div>
    </div>
  );
}

function IncomeEditor({ initial, sectionId, onClose, onSaved }) {
  const isNew = !initial?.id;
  const [form, setForm] = useState({
    date: initial?.date || new Date().toISOString().slice(0, 10),
    concept: initial?.concept || "",
    student_id: initial?.student_id || "",
    student_name: initial?.student_name || "",
    base_amount: initial?.base_amount ?? 0,
    vat_percent: initial?.vat_percent ?? 21,
    vat_amount: initial?.vat_amount ?? 0,
    total_amount: initial?.total_amount ?? 0,
    income_account: initial?.income_account || "",
    payment_method: initial?.payment_method || "cash",
    invoice_number: initial?.invoice_number || "",
    notes: initial?.notes || "",
  });
  const [saving, setSaving] = useState(false);

  // Auto-cálculo IVA + total al cambiar base o %
  useEffect(() => {
    const base = Number(form.base_amount) || 0;
    const pct = Number(form.vat_percent) || 0;
    const vat = Math.round((base * pct) * 100) / 10000 * 100; // base * pct / 100
    const vatR = Math.round((base * pct / 100) * 100) / 100;
    const total = Math.round((base + vatR) * 100) / 100;
    void vat;
    setForm((f) => ({ ...f, vat_amount: vatR, total_amount: total }));
   
  }, [form.base_amount, form.vat_percent]);

  const save = async () => {
    if (!form.concept.trim() || !form.date) {
      toast.error("Concepto y fecha son obligatorios");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, section_id: sectionId || "" };
      if (isNew) {
        await api.post("/accounting/incomes", payload);
        toast.success("Ingreso registrado");
      } else {
        await api.patch(`/accounting/incomes/${initial.id}`, payload);
        toast.success("Ingreso actualizado");
      }
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Error");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={onClose}
         data-testid="income-editor-dialog">
      <div className="bg-white rounded-2xl w-full max-w-lg p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-lg">{isNew ? "Nuevo ingreso" : "Editar ingreso"}</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-neutral-100"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <FieldInput label="Fecha *" type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} testid="income-date" />
            <FieldSelect label="Cobro" value={form.payment_method} onChange={(v) => setForm({ ...form, payment_method: v })} testid="income-method"
                          options={[["cash", "Efectivo"], ["card", "Tarjeta"], ["transfer", "Transferencia"], ["bizum", "Bizum"], ["other", "Otro"]]} />
          </div>
          <FieldInput label="Concepto *" value={form.concept} onChange={(v) => setForm({ ...form, concept: v })} testid="income-concept" placeholder="Ej: Matrícula Permiso B" />
          <div className="grid grid-cols-2 gap-3">
            <FieldInput label="Alumno" value={form.student_name} onChange={(v) => setForm({ ...form, student_name: v })} placeholder="Nombre (opcional)" testid="income-student" />
            <FieldInput label="Nº Factura" value={form.invoice_number} onChange={(v) => setForm({ ...form, invoice_number: v })} placeholder="F-2026-…" testid="income-invoice" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <FieldInput label="Base (€)" type="number" step="0.01" value={form.base_amount} onChange={(v) => setForm({ ...form, base_amount: v })} testid="income-base" />
            <FieldInput label="IVA %" type="number" step="1" value={form.vat_percent} onChange={(v) => setForm({ ...form, vat_percent: v })} testid="income-vatpct" />
            <FieldInput label="Total (€)" type="number" step="0.01" value={form.total_amount} onChange={(v) => setForm({ ...form, total_amount: v })} testid="income-total" />
          </div>
          <FieldInput label="Cuenta de ingreso" value={form.income_account} onChange={(v) => setForm({ ...form, income_account: v })} placeholder="Formación, Tienda…" testid="income-account" />
          <FieldTextarea label="Notas" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testid="income-notes" />
        </div>
        <div className="flex items-center justify-end gap-2 mt-5">
          <button onClick={onClose} className="btn-secondary !h-9 !px-3 !text-xs">Cancelar</button>
          <button onClick={save} disabled={saving} className="btn-primary !h-9 !px-4 !text-xs" data-testid="income-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Guardar
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * V1.39 · Panel Bancos
 * ────────────────────────────────────────────────────────── */
export function BankPanel({ sectionId, sectionLabel }) {
  const pin = usePinSecurity();
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ credits: 0, debits: 0, net: 0 });
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filters, setFilters] = useState({ year: CURRENT_YEAR, quarter: "", reconciled: "", bank_name: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 300 };
      Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v !== null) params[k] = v; });
      if (sectionId) params.section_id = sectionId;
      const { data } = await api.get("/accounting/bank-movements", { params });
      setItems(data?.items || []);
      setTotals(data?.totals || { credits: 0, debits: 0, net: 0 });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Error");
    } finally { setLoading(false); }
  }, [filters, sectionId]);
  useEffect(() => { load(); }, [load]);

  const remove = async (row) => {
    try {
      await pin.require("accounting.bank.delete");
      if (!window.confirm(`¿Eliminar movimiento "${row.concept}"?`)) return;
      await api.delete(`/accounting/bank-movements/${row.id}`);
      toast.success("Movimiento eliminado");
      load();
    } catch (err) {
      const m = formatApiError(err?.response?.data?.detail) || err?.message;
      if (m && !/cancel/i.test(m)) toast.error(m);
    }
  };

  const toggleReconciled = async (row) => {
    try {
      await api.patch(`/accounting/bank-movements/${row.id}`, { reconciled: !row.reconciled });
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Error"); }
  };

  return (
    <div className="space-y-3" data-testid="bank-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap text-xs text-neutral-500">
          {sectionLabel && (
            <span className="inline-flex items-center gap-1">
              <Layers className="h-3.5 w-3.5 text-orange-600" />
              <span className="rounded-full bg-orange-100 text-orange-800 px-2 py-0.5 font-bold">{sectionLabel}</span>
            </span>
          )}
          <span className="inline-flex items-center gap-1"><Landmark className="h-3.5 w-3.5" /> Movimientos {filters.year}</span>
        </div>
        <button onClick={() => setOpenNew(true)} className="btn-primary !h-9 !px-3 !text-xs" data-testid="bank-new-btn">
          <Plus className="h-4 w-4" /> Nuevo movimiento
        </button>
      </div>

      <div className="flex items-center gap-2 flex-wrap text-xs">
        <div className="inline-flex items-center gap-1 text-neutral-500"><Filter className="h-3 w-3" /> Filtros:</div>
        <select value={filters.year} onChange={(e) => setFilters({ ...filters, year: parseInt(e.target.value, 10) })}
                className="h-8 rounded-lg border border-neutral-300 bg-white px-2 text-xs">
          {[CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1].map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <select value={filters.quarter} onChange={(e) => setFilters({ ...filters, quarter: e.target.value })}
                className="h-8 rounded-lg border border-neutral-300 bg-white px-2 text-xs">
          <option value="">Todos los trimestres</option>
          {[1, 2, 3, 4].map((q) => <option key={q} value={q}>T{q}</option>)}
        </select>
        <select value={filters.reconciled} onChange={(e) => setFilters({ ...filters, reconciled: e.target.value })}
                className="h-8 rounded-lg border border-neutral-300 bg-white px-2 text-xs">
          <option value="">Conciliación: todos</option>
          <option value="true">Solo conciliados</option>
          <option value="false">Solo sin conciliar</option>
        </select>
      </div>

      {loading ? (
        <div className="text-center text-sm text-neutral-500 py-8"><Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Cargando…</div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <TotalCard label="Abonos" value={MONEY(totals.credits)} color="emerald" />
            <TotalCard label="Cargos" value={MONEY(totals.debits)} color="red" />
            <TotalCard label="Neto" value={MONEY(totals.net)} color={totals.net >= 0 ? "emerald" : "red"} />
          </div>
          <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-[10px] uppercase tracking-wider text-neutral-500">
                <tr>
                  <th className="text-left px-3 py-2 w-24">Fecha</th>
                  <th className="text-left px-3 py-2 w-32">Banco</th>
                  <th className="text-left px-3 py-2">Concepto</th>
                  <th className="text-right px-3 py-2 w-28">Importe</th>
                  <th className="text-right px-3 py-2 w-28">Saldo</th>
                  <th className="text-center px-3 py-2 w-20">Concil.</th>
                  <th className="w-16"></th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={7} className="text-center text-sm text-neutral-500 py-8">Sin movimientos.</td></tr>
                ) : items.map((row) => (
                  <tr key={row.id} className="border-t border-neutral-100 hover:bg-neutral-50/50" data-testid={`bank-row-${row.id}`}>
                    <td className="px-3 py-2 tabular-nums">{row.date}</td>
                    <td className="px-3 py-2 text-xs">
                      <div className="font-semibold">{row.bank_name}</div>
                      {row.account_iban && <div className="text-[10px] text-neutral-500 tabular-nums">{row.account_iban}</div>}
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{row.concept}</div>
                      {row.category && <div className="text-[10px] text-neutral-500">{row.category}</div>}
                    </td>
                    <td className={"px-3 py-2 text-right tabular-nums font-semibold " + (row.amount >= 0 ? "text-emerald-700" : "text-red-700")}>
                      {row.amount >= 0 ? "+" : ""}{MONEY(row.amount)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-neutral-500">{MONEY(row.balance)}</td>
                    <td className="px-3 py-2 text-center">
                      <input type="checkbox" checked={!!row.reconciled} onChange={() => toggleReconciled(row)}
                              data-testid={`bank-reconcile-${row.id}`} />
                    </td>
                    <td className="px-3 py-2 text-right space-x-1 whitespace-nowrap">
                      <button onClick={() => setEditing(row)} className="text-xs text-neutral-600 hover:text-orange-600" data-testid={`bank-edit-${row.id}`}>Editar</button>
                      <button onClick={() => remove(row)} className="text-xs text-red-600 hover:text-red-800" data-testid={`bank-del-${row.id}`}>
                        <Trash2 className="h-3.5 w-3.5 inline" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {(openNew || editing) && (
        <BankEditor
          initial={editing || {}}
          sectionId={sectionId}
          onClose={() => { setOpenNew(false); setEditing(null); }}
          onSaved={() => { setOpenNew(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function BankEditor({ initial, sectionId, onClose, onSaved }) {
  const isNew = !initial?.id;
  const [form, setForm] = useState({
    date: initial?.date || new Date().toISOString().slice(0, 10),
    bank_name: initial?.bank_name || "",
    account_iban: initial?.account_iban || "",
    concept: initial?.concept || "",
    amount: initial?.amount ?? 0,
    balance: initial?.balance ?? 0,
    category: initial?.category || "",
    reconciled: initial?.reconciled || false,
    notes: initial?.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.bank_name.trim() || !form.concept.trim() || !form.date) {
      toast.error("Fecha, banco y concepto son obligatorios");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, amount: Number(form.amount), balance: Number(form.balance), section_id: sectionId || "" };
      if (isNew) {
        await api.post("/accounting/bank-movements", payload);
        toast.success("Movimiento registrado");
      } else {
        await api.patch(`/accounting/bank-movements/${initial.id}`, payload);
        toast.success("Movimiento actualizado");
      }
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Error");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={onClose}
         data-testid="bank-editor-dialog">
      <div className="bg-white rounded-2xl w-full max-w-lg p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-lg">{isNew ? "Nuevo movimiento bancario" : "Editar movimiento"}</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-neutral-100"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <FieldInput label="Fecha *" type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} testid="bank-date" />
            <FieldInput label="Banco *" value={form.bank_name} onChange={(v) => setForm({ ...form, bank_name: v })} placeholder="La Caixa" testid="bank-name" />
          </div>
          <FieldInput label="IBAN / últimos dígitos" value={form.account_iban} onChange={(v) => setForm({ ...form, account_iban: v })} placeholder="…1234" testid="bank-iban" />
          <FieldInput label="Concepto *" value={form.concept} onChange={(v) => setForm({ ...form, concept: v })} testid="bank-concept" />
          <div className="grid grid-cols-3 gap-3">
            <FieldInput label="Importe (€)" type="number" step="0.01" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} testid="bank-amount" hint="+ abono / − cargo" />
            <FieldInput label="Saldo (€)" type="number" step="0.01" value={form.balance} onChange={(v) => setForm({ ...form, balance: v })} testid="bank-balance" />
            <FieldInput label="Categoría" value={form.category} onChange={(v) => setForm({ ...form, category: v })} placeholder="combustible…" testid="bank-category" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.reconciled} onChange={(e) => setForm({ ...form, reconciled: e.target.checked })}
                   data-testid="bank-reconciled" />
            Movimiento conciliado
          </label>
          <FieldTextarea label="Notas" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testid="bank-notes" />
        </div>
        <div className="flex items-center justify-end gap-2 mt-5">
          <button onClick={onClose} className="btn-secondary !h-9 !px-3 !text-xs">Cancelar</button>
          <button onClick={save} disabled={saving} className="btn-primary !h-9 !px-4 !text-xs" data-testid="bank-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Guardar
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * V1.39 · Panel IVA trimestral (soportado vs repercutido)
 * ────────────────────────────────────────────────────────── */
export function VatPanel({ sectionId, sectionLabel }) {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = { year };
        if (sectionId) params.section_id = sectionId;
        const { data } = await api.get("/accounting/reports/vat-quarterly", { params });
        setData(data);
      } catch (err) {
        toast.error(formatApiError(err?.response?.data?.detail) || "Error");
        setData(null);
      } finally { setLoading(false); }
    })();
  }, [year, sectionId]);

  return (
    <div className="space-y-3" data-testid="vat-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap text-xs text-neutral-500">
          {sectionLabel && (
            <span className="inline-flex items-center gap-1">
              <Layers className="h-3.5 w-3.5 text-orange-600" />
              <span className="rounded-full bg-orange-100 text-orange-800 px-2 py-0.5 font-bold">{sectionLabel}</span>
            </span>
          )}
          <span className="inline-flex items-center gap-1"><Percent className="h-3.5 w-3.5" /> IVA {year}</span>
        </div>
        <select value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))}
                className="h-9 rounded-lg border border-neutral-300 bg-white px-3 text-xs" data-testid="vat-year">
          {[CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1].map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="text-center text-sm text-neutral-500 py-8"><Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Cargando…</div>
      ) : !data ? (
        <div className="text-center text-sm text-neutral-500 py-8">Sin datos.</div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <TotalCard label="IVA soportado (gastos)" value={MONEY(data.totals.supported)} color="red" />
            <TotalCard label="IVA repercutido (ingresos)" value={MONEY(data.totals.charged)} color="emerald" />
            <TotalCard label={data.totals.balance >= 0 ? "A ingresar en Hacienda" : "A compensar"}
                        value={MONEY(Math.abs(data.totals.balance))}
                        color={data.totals.balance >= 0 ? "orange" : "neutral"} />
          </div>
          <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-[10px] uppercase tracking-wider text-neutral-500">
                <tr>
                  <th className="text-left px-3 py-2 w-20">Trim.</th>
                  <th className="text-right px-3 py-2">Base gastos</th>
                  <th className="text-right px-3 py-2 border-r border-neutral-200">IVA soportado</th>
                  <th className="text-right px-3 py-2">Base ingresos</th>
                  <th className="text-right px-3 py-2 border-r border-neutral-200">IVA repercutido</th>
                  <th className="text-right px-3 py-2">Saldo trim.</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.quarter} className="border-t border-neutral-100" data-testid={`vat-q${r.quarter}`}>
                    <td className="px-3 py-2 font-bold">T{r.quarter}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-neutral-500">{MONEY(r.expenses.base)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold text-red-700 border-r border-neutral-100">{MONEY(r.expenses.vat)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-neutral-500">{MONEY(r.incomes.base)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold text-emerald-700 border-r border-neutral-100">{MONEY(r.incomes.vat)}</td>
                    <td className={"px-3 py-2 text-right tabular-nums font-bold " + (r.balance >= 0 ? "text-orange-700" : "text-neutral-600")}>
                      {r.balance >= 0 ? "+" : ""}{MONEY(r.balance)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-neutral-300 bg-neutral-50 font-bold">
                  <td className="px-3 py-2">Año</td>
                  <td className="px-3 py-2 text-right tabular-nums text-neutral-500" colSpan={1}>—</td>
                  <td className="px-3 py-2 text-right tabular-nums text-red-800 border-r border-neutral-200">{MONEY(data.totals.supported)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-neutral-500">—</td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-800 border-r border-neutral-200">{MONEY(data.totals.charged)}</td>
                  <td className={"px-3 py-2 text-right tabular-nums " + (data.totals.balance >= 0 ? "text-orange-800" : "text-neutral-700")}>
                    {data.totals.balance >= 0 ? "+" : ""}{MONEY(data.totals.balance)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="text-[11px] text-neutral-500 leading-snug">
            <b>Saldo positivo</b> = IVA repercutido &gt; IVA soportado → cantidad <b>a ingresar en Hacienda</b> ese trimestre.
            <b> Saldo negativo</b> = IVA soportado &gt; IVA repercutido → cantidad <b>a compensar</b> en trimestres siguientes.
          </div>
        </>
      )}
    </div>
  );
}

/* ─── Helpers UI compartidos ─── */
function TotalCard({ label, value, color }) {
  const map = {
    neutral: "bg-neutral-50 text-neutral-800 border-neutral-200",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    red: "bg-red-50 text-red-800 border-red-200",
    orange: "bg-orange-50 text-orange-800 border-orange-200",
  };
  return (
    <div className={"rounded-xl border px-3 py-2 " + (map[color] || map.neutral)}>
      <div className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{label}</div>
      <div className="text-lg font-bold tabular-nums">{value}</div>
    </div>
  );
}

function PaymentBadge({ method }) {
  const map = {
    cash: ["bg-neutral-100 text-neutral-800", "Efectivo"],
    card: ["bg-blue-100 text-blue-800", "Tarjeta"],
    transfer: ["bg-emerald-100 text-emerald-800", "Transf."],
    bizum: ["bg-orange-100 text-orange-800", "Bizum"],
    other: ["bg-neutral-100 text-neutral-500", "Otro"],
  };
  const [cls, label] = map[method] || map.other;
  return <span className={"inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold " + cls}>{label}</span>;
}

function FieldInput({ label, type = "text", value, onChange, placeholder, testid, hint, step }) {
  return (
    <div>
      <label className="text-xs font-semibold text-neutral-600 block mb-1">{label}</label>
      <input type={type} step={step} value={value ?? ""} onChange={(e) => onChange(type === "number" ? e.target.value : e.target.value)}
             placeholder={placeholder} data-testid={testid}
             className="w-full h-10 rounded-lg border border-neutral-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
      {hint && <div className="text-[10px] text-neutral-500 mt-0.5">{hint}</div>}
    </div>
  );
}
function FieldTextarea({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold text-neutral-600 block mb-1">{label}</label>
      <textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={2} data-testid={testid}
                className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
    </div>
  );
}
function FieldSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold text-neutral-600 block mb-1">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
              className="w-full h-10 rounded-lg border border-neutral-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}
